import { FC, memo, useCallback, useEffect, useReducer, useRef, useState } from 'react';
import Konva from 'konva';
import { Scene } from 'types/Scene';
import { useDispatch, useSelector } from 'react-redux';
import { addCurrentTextId, getCurrentTextId, setCurrentTextId } from 'modules/currentTextId';
import { ActiveTool, ToolName } from 'types/AppState';
import { Effect } from 'types/Effects';
import { updateEffectParams, updateEffectsParams } from 'modules/effects';
import { ActionTypes, reducer } from './konvaContainer/reducer';
import { TextareaComponent } from './konvaContainer/TextareaComponent';
import {
  BackgroundChooser,
  createBackgroundChooser,
  HOVER_RECT_ID
} from './konvaContainer/backgroundChooser';
import { setCurrentScene } from 'modules/scenes';
import {
  createHoverBorder,
  createTransformer,
  HOVER_BORDER_ID,
  transformEndHandler,
  transformHandler
} from './konvaContainer/transformer';
import { getLines, getTextMaxWidth } from 'utils/konva';
import { isEmpty, isNil } from 'ramda';
import { debounce } from 'throttle-debounce';
import { ARROW_TEXT_MOVE_STEP, ARROW_TEXT_MOVE_STEP_LARGE } from 'constants/textTool';
import { isInputFocused } from 'utils/clipboard';
import { getCurrentWorkspace } from 'modules/workspace';
import { Workspace } from 'types/Clipboard';
import analytics, { Shortcuts } from 'utils/analytics';
import textSceneFunc from 'utils/konva/textSceneFunc';
import { setKonvaLayer as setStoreKonvaLayer } from 'modules/canvas';
import effectToKonvaTextSettings from 'utils/konva/effectToKonvaTextSettings';

import { saveRevision } from 'modules/revisions';
import { isModalOpened } from 'shared/Modal';
import { getTransformedText } from 'utils/textTool';

const STAGE_ID = 'konvaStage';

export type KonvaContainerProps = {
  width: number;
  height: number;
  scene: Scene;
  playing: boolean;
  effects: Effect[];
  loadedFonts: Array<string>;
  isReplacing: boolean;
  isReplaceAvailable: boolean;
  scale: number;
  setReplaceAvailableHandler: (val: boolean) => void;
  handleCancelReplace: () => void;
};

export const KonvaContainer: FC<KonvaContainerProps & { activeTool: ActiveTool }> = memo(
  ({
    width,
    height,
    scene,
    playing,
    effects,
    activeTool,
    loadedFonts,
    isReplaceAvailable,
    isReplacing,
    setReplaceAvailableHandler,
    handleCancelReplace,
    scale
  }) => {
    const ref = useRef(null);
    const currentTextIds = useSelector(getCurrentTextId);
    const [konvaStage, setKonvaStage] = useState(null);
    const [konvaLayer, setKonvaLayer] = useState(null);
    const [state, dispatchAction] = useReducer(reducer, {
      isTransforming: false,
      isEditing: false,
      updateText: false
    });
    const [backgroundChooser, setBackgroundChooser] = useState<BackgroundChooser>(null);
    const [transformer, setTransformer] = useState<Konva.Transformer>(null);
    const currentWorkspace = useSelector(getCurrentWorkspace);
    const dispatch = useDispatch();

    const debouncedSaveRevision = debounce(100, () => {
      dispatch(saveRevision(ToolName.Text));
    });

    useEffect(() => {
      dispatch(setStoreKonvaLayer(konvaLayer));
    }, [konvaLayer]);

    useEffect(() => {
      const onKeyDown = (evt: KeyboardEvent) => {
        if (evt.code === 'Escape' && (isReplaceAvailable || isReplacing)) {
          handleCancelReplace();
        }
      };

      const handleClick = (event: MouseEvent) => {
        const konvaCanvas = document.querySelector('div[class=konvajs-content] > canvas');
        const el = event.target as Element;

        const settings = el.closest('div[data-test=settings]');
        const toolPanelPhoto = el.closest('div[data-test=tool-panel_photo]');
        const toolPanelVideo = el.closest('div[data-test=tool-panel_stock-videos]');
        const clickedInsideSettings = !isNil(settings);
        const clickedInsideToolPanelPhoto = !isNil(toolPanelPhoto);
        if (
          (el !== konvaCanvas &&
            !clickedInsideSettings &&
            !clickedInsideToolPanelPhoto &&
            !toolPanelVideo &&
            (isReplaceAvailable || isReplacing)) ||
          (state.isTransforming && el === konvaCanvas)
        ) {
          handleCancelReplace();
        }

        if (el !== konvaCanvas && !state.isEditing) {
          const artBoard = el.closest('div[data-test=artBoard]');
          const clickedInsideArtBoard = !isNil(artBoard);

          if (clickedInsideArtBoard && !clickedInsideSettings) {
            dispatchAction({ type: ActionTypes.TransformEnd });
            dispatch(setCurrentTextId([]));
          }
        }
      };

      window.addEventListener('click', handleClick);
      window.addEventListener('keydown', onKeyDown);
      return () => {
        window.removeEventListener('click', handleClick);
        window.removeEventListener('keydown', onKeyDown);
      };
    }, [isReplaceAvailable, isReplacing, state.isTransforming, currentTextIds, state.isEditing]);

    // move text shortcuts
    useEffect(() => {
      if (!transformer || !konvaLayer || currentWorkspace !== Workspace.Artboard) return;

      const rects: [Konva.Rect, string][] = currentTextIds.map(id => [
        konvaLayer.findOne(`#${id}`),
        id
      ]);

      const onKeyRelease = () => {
        if (isInputFocused()) return;
        window.removeEventListener('keyup', onKeyRelease);

        const params = rects.map(([rect, id]) => {
          return {
            id,
            position: { x: rect.attrs.x, y: rect.attrs.y }
          };
        });
        dispatch(updateEffectsParams(scene.id, params));
        analytics.shortcut({ item: ToolName.Text, hotkey_command: Shortcuts.TextMove });
        dispatch(saveRevision(ToolName.Text));
      };

      const onKeyDown = (evt: KeyboardEvent) => {
        /* This code makes tool-panel not to scroll when we move added text with arrow keys */
        if (document.activeElement.nodeName === 'BODY') {
          const container = konvaStage.container();
          container.tabIndex = 1;
          container.focus();
        }

        if (
          !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(evt.code) ||
          isInputFocused() ||
          isModalOpened()
        ) {
          return;
        }

        if (!evt.repeat) {
          window.addEventListener('keyup', onKeyRelease);
        }

        rects.forEach(([rect]) => {
          const attrs: any = { x: 0, y: 0 };
          const step = evt.shiftKey ? ARROW_TEXT_MOVE_STEP_LARGE : ARROW_TEXT_MOVE_STEP;

          if (evt.code === 'ArrowLeft') {
            attrs.x = -step;
          }
          if (evt.code === 'ArrowRight') {
            attrs.x = step;
          }
          if (evt.code === 'ArrowUp') {
            attrs.y = -step;
          }
          if (evt.code === 'ArrowDown') {
            attrs.y = step;
          }
          rect.setAttrs({
            x: attrs.x + rect.attrs.x,
            y: attrs.y + rect.attrs.y
          });
        });

        konvaLayer.draw();
      };

      window.addEventListener('keydown', onKeyDown);
      return () => {
        window.removeEventListener('keydown', onKeyDown);
        window.removeEventListener('keyup', onKeyRelease);
      };
    }, [currentWorkspace, currentTextIds, effects, transformer, konvaLayer, scene, konvaStage]);

    const addGroupOnLayer = () => {
      const konvaGroup = new Konva.Group({
        id: scene.id,
        size: { width, height },
        scale: { x: scale, y: scale },
        position: { x: 0, y: 0 }
      });
      konvaLayer.add(konvaGroup);
      konvaLayer.draw();
    };

    const addTextOnScene = () => {
      if (!konvaLayer) return;
      const konvaGroup = konvaLayer.findOne(`#${scene.id}`);

      effects?.forEach(effect => {
        const {
          id,
          name,
          position: { x, y }
        } = effect;
        if (name !== 'text') return;
        const prevText = konvaLayer.findOne(`#${id}`);
        prevText?.remove();

        const text = new Konva.Text(effectToKonvaTextSettings(effect));
        text._sceneFunc = textSceneFunc;

        konvaGroup.add(text);

        text.on('click', e => {
          if (currentTextIds.includes(id)) {
            dispatch(setCurrentTextId([id]));
            konvaStage.container().children[0].style.cursor = 'default';
            dispatchAction({ type: ActionTypes.StartEditing });
            return;
          }
          if (text === e.target) {
            if (e.evt.shiftKey) {
              dispatchAction({ type: ActionTypes.MouseClick });
              dispatch(addCurrentTextId(id));
            } else {
              dispatchAction({ type: ActionTypes.MouseClick });
              dispatch(setCurrentTextId([id]));
            }
          }
        });

        text.on('mouseenter', ({ target }) => {
          konvaStage.container().children[0].style.cursor = 'move';
          const transformerAttachedId = transformer?.getAttr('attachedId') || [];
          if (transformerAttachedId.includes(target.id())) {
            return;
          }
          konvaStage.findOne(`#${HOVER_BORDER_ID}`)?.remove();
          const hoverBorder = createHoverBorder(target.id());
          konvaLayer.add(hoverBorder);
          hoverBorder.nodes([...hoverBorder.getNodes(), target]);
          hoverBorder.setAttr('attachedId', target.id());
          konvaLayer.draw();
        });

        text.on('mouseleave', () => {
          const hoverBorders = konvaStage.find(`#${HOVER_BORDER_ID}`).toArray();
          hoverBorders?.forEach((tr: Konva.Transformer) => tr.destroy());
          konvaStage.draw();
          konvaStage.container().children[0].style.cursor = 'default';
        });

        text.on('dragend', ({ target }) => {
          const { x: textX, y: textY } = target.getAttrs();

          if (x !== textX || y !== textY) {
            dispatch(
              updateEffectParams(scene.id, id, {
                position: { x: textX, y: textY }
              })
            );
            debouncedSaveRevision();
          }
        });

        text.on('dragstart', () => {
          const hoverBorders = konvaStage.find(`#${HOVER_BORDER_ID}`).toArray();
          hoverBorders?.forEach((tr: Konva.Transformer) => tr.destroy());
          konvaStage.draw();
        });
      });
    };

    const handleStopEditing = useCallback(
      (sceneId, textId, text) => {
        const { _context: ctx } = konvaLayer.getContext();
        const effect = effects.find(item => item.id === textId);
        const {
          font: { fontFamily },
          fontSize
        } = effect;

        const font = `${fontSize}px ${fontFamily}`;

        ctx.font = font;

        const transformedText = getTransformedText(effect.textCaps, text);
        const textCaps = effect.textCaps;

        const textEffect = {
          ...effect,
          transformedText,
          textCaps
        };
        const textArr = getLines(textEffect);
        dispatch(
          updateEffectParams(sceneId, textId, {
            textArr,
            text,
            transformedText,
            width: getTextMaxWidth(textArr, fontFamily, fontSize)
          })
        );
        dispatch(saveRevision(ToolName.Text));

        const konvaText = konvaLayer.findOne(`#${textId}`);
        const transformer = konvaLayer.findOne('#defaultTransformer');
        transformer.nodes([konvaText]);
        transformer.setAttr('attachedId', textId);

        konvaLayer.draw();

        dispatchAction({ type: ActionTypes.Reset });
      },
      [konvaLayer, effects]
    );

    const handleCancelEditing = useCallback(() => {
      const konvaRect = konvaLayer.findOne(`#${currentTextIds[0]}`);
      konvaRect.show();

      konvaLayer.draw();

      dispatchAction({ type: ActionTypes.Reset });
      dispatch(setCurrentTextId([]));
    }, [konvaLayer, currentTextIds]);

    // on mount: init Konva, stage and layer
    useEffect(() => {
      const stage = new Konva.Stage({
        id: STAGE_ID,
        container: ref.current
      });
      stage.content.style.position = 'absolute';
      setKonvaStage(stage);

      const layer = new Konva.Layer();
      setKonvaLayer(layer);

      stage.add(layer);
      layer.draw();
    }, [width, height]);

    useEffect(() => {
      if (!konvaLayer) return;
      konvaLayer.draw();
    }, [loadedFonts]);

    // disable transform when click outside box
    useEffect(() => {
      if (!konvaLayer || !currentTextIds) return;

      const handleMouseClickStage = ({ target }: Konva.KonvaEventObject<MouseEvent>) => {
        const id = target.getAttr('id');

        if (!state.isEditing && id && (id === STAGE_ID || id === HOVER_RECT_ID)) {
          dispatchAction({ type: ActionTypes.TransformEnd });
          dispatch(setCurrentTextId([]));
        }
      };
      konvaStage.on('click', handleMouseClickStage);
      return () => {
        konvaStage.off('click', handleMouseClickStage);
      };
    }, [konvaStage, konvaLayer, currentTextIds, state.isTransforming, state.isEditing]);

    // reset transformer state when text was deleted
    useEffect(() => {
      if (isEmpty(currentTextIds) && state.isTransforming) {
        dispatchAction({ type: ActionTypes.TransformEnd });
      }
    }, [currentTextIds]);

    useEffect(() => {
      if (!konvaLayer || !transformer) return;
      if ((!currentTextIds && !state.isTransforming) || state.isEditing) {
        transformer.setAttr('attachedId', null);
        transformer.nodes([]);
      } else {
        const konvaText = currentTextIds
          .map(id => konvaLayer.findOne(`#${id}`))
          .filter(rect => rect);
        transformer.setAttr('attachedId', currentTextIds);
        const hoverBorder = konvaStage.findOne(`#${HOVER_BORDER_ID}`);
        hoverBorder?.destroy();
        transformer.nodes(konvaText);
        transformer.moveToTop();
      }

      konvaLayer.draw();
    }, [state.isTransforming, state.isEditing, currentTextIds, konvaLayer, transformer]);

    useEffect(() => {
      if (!transformer) return;
      const updateEffects = (param: Partial<Effect>[]) => {
        dispatch(updateEffectsParams(scene.id, param));
        dispatch(saveRevision(ToolName.Text));
      };

      const onTransformHandler = transformHandler(transformer, konvaLayer, currentTextIds, effects);

      const onTransformEndHandler = transformEndHandler(
        transformer,
        konvaLayer,
        currentTextIds,
        effects,
        updateEffects,
        () => {
          konvaStage.container().children[0].style.cursor = 'default';
        }
      );
      transformer.on('transform', onTransformHandler);
      transformer.on('transformend', onTransformEndHandler);
      return () => {
        transformer.off('transform', onTransformHandler);
        transformer.off('transformend', onTransformEndHandler);
      };
    }, [currentTextIds, transformer]);

    // on editing text
    useEffect(() => {
      if (!konvaLayer || !currentTextIds || !state.updateText || !state.isEditing) return;

      if (state.isEditing) {
        currentTextIds.forEach(textId => {
          const konvaText = konvaLayer.findOne(`#${textId}`);
          if (!konvaText) return;
          konvaText.hide();
          konvaLayer.draw();
        });
      }
    }, [state.isEditing, currentTextIds, state.updateText, konvaLayer]);

    // on play/pause we should hide/show Konva.canvas
    useEffect(() => {
      if (!konvaLayer) return;

      konvaLayer.setAttr('opacity', playing ? 0 : 1);
      konvaLayer.draw();
    }, [konvaLayer, playing]);

    // apply changed Player.canvas.size to Konva.canvas
    useEffect(() => {
      if (!konvaStage || !konvaLayer) return;
      konvaStage.size({ width, height });
      backgroundChooser?.update();
      konvaLayer.draw();
    }, [konvaStage, konvaLayer, width, height]);

    // redraw effects when they changes
    useEffect(() => {
      if (!konvaLayer) return;
      konvaLayer.removeChildren();
      setTransformer(createTransformer(konvaLayer));
      addGroupOnLayer();
      setBackgroundChooser(
        createBackgroundChooser(konvaLayer, scene.id, scene.size, width, height, scene.scalingMode)
      );
      addTextOnScene();
    }, [konvaLayer, effects, scene, scale, activeTool, width, height, currentTextIds]); // we need to to re-draw when scenes, effects changes

    useEffect(() => {
      dispatchAction({ type: ActionTypes.Reset });
    }, [scene, effects]);

    useEffect(() => {
      if (!backgroundChooser) return;
      const handleMouseOver = ({ target }: Konva.KonvaEventObject<MouseEvent>) => {
        backgroundChooser.setHoverRectVisible(
          isReplaceAvailable || isReplacing || target.id() === HOVER_RECT_ID
        );
      };

      const handleMouseLeave = () => {
        if (isReplaceAvailable) return;
        backgroundChooser.setHoverRectVisible(false);
      };

      const handleClick = (event: Konva.KonvaEventObject<MouseEvent>) => {
        if (event.target.id() === HOVER_RECT_ID) {
          if (!isReplacing && !isReplaceAvailable && !state.isEditing && !state.isTransforming) {
            setReplaceAvailableHandler(true);
            dispatch(setCurrentScene(scene.id));
          } else {
            handleCancelReplace();
          }
        }
      };

      konvaLayer.on('mouseleave', handleMouseLeave);
      konvaLayer.on('mouseover', handleMouseOver);
      konvaLayer.on('click', handleClick);
      return () => {
        konvaLayer.off('mouseleave', handleMouseLeave);
        konvaLayer.off('mouseover', handleMouseOver);
        konvaLayer.off('click', handleClick);
      };
    }, [backgroundChooser, isReplaceAvailable, isReplacing, state.isEditing, state.isTransforming]);

    useEffect(() => {
      backgroundChooser?.setHoverRectVisible(isReplaceAvailable);
      backgroundChooser?.setBackgroundVisible(isReplacing);
    }, [isReplaceAvailable, isReplacing, backgroundChooser]);

    useEffect(() => {
      setReplaceAvailableHandler(false);
    }, [scene.id]);

    const rectText = konvaLayer?.findOne(`#${currentTextIds[0]}`);
    const effectForEdit = effects?.find(item => item.id === currentTextIds[0]);
    return (
      <div>
        <div ref={ref} sx={{ position: 'absolute', top: 0, left: 0, zIndex: 2 }} />
        {state.isEditing && rectText && effectForEdit && (
          <TextareaComponent
            konvaText={rectText}
            text={effectForEdit}
            handleStopEditing={handleStopEditing}
            handleCancelEditing={handleCancelEditing}
          />
        )}
      </div>
    );
  }
);
